import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  Clock,
  Coins,
  FileText,
  Users,
  Wallet,
} from 'lucide-react';
import type { CargoOverview } from '../../api/types';
import { CargoMetricCard } from './CargoMetricCard';

type CargoStatsGridProps = {
  stats: CargoOverview | null;
  loading: boolean;
};

function parseCount(raw?: string | null): number {
  const n = Number.parseInt(String(raw ?? ''), 10);
  return Number.isFinite(n) ? n : 0;
}

function formatCount(raw?: string | null, loading = false): string {
  if (loading) return '…';
  if (raw == null || raw === '') return '—';
  return parseCount(raw).toLocaleString('en-US');
}

function formatUsdtMetric(raw?: string | null, loading = false): { value: string; unit: string } {
  if (loading) return { value: '…', unit: '' };
  const n = Number.parseFloat(String(raw ?? ''));
  if (!Number.isFinite(n) || n === 0) return { value: '0', unit: 'USDT' };
  if (n >= 1_000_000) {
    return { value: (n / 1_000_000).toLocaleString('en-US', { maximumFractionDigits: 2 }), unit: 'M USDT' };
  }
  if (n >= 10_000) {
    return { value: n.toLocaleString('en-US', { maximumFractionDigits: 0 }), unit: 'USDT' };
  }
  return { value: n.toLocaleString('en-US', { maximumFractionDigits: 2 }), unit: 'USDT' };
}

function pct(part: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((part / total) * 100);
}

export function CargoStatsGrid({ stats, loading }: CargoStatsGridProps) {
  const companies = parseCount(stats?.companies);
  const pendingApproval = parseCount(stats?.pending_approval);
  const suspended = parseCount(stats?.suspended);
  const totalInvoices = parseCount(stats?.invoices);
  const paidInvoices = parseCount(stats?.paid);
  const pendingInvoices = parseCount(stats?.pending);
  const splitFailed = parseCount(stats?.split_failed);
  const amlBlocked = parseCount(stats?.aml_failed);
  const noWallet = parseCount(stats?.no_wallet);
  const paidRate = pct(paidInvoices, totalInvoices);

  const volume = formatUsdtMetric(stats?.volume_usdt, loading);
  const commission = formatUsdtMetric(stats?.commission_usdt, loading);

  const partnerHint =
    pendingApproval > 0 || suspended > 0
      ? [
          pendingApproval > 0 ? `${pendingApproval.toLocaleString()} awaiting approval` : null,
          suspended > 0 ? `${suspended.toLocaleString()} suspended` : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : 'All partners onboarded';

  const issueCount = splitFailed + amlBlocked + noWallet;

  return (
    <div className="cargo-metrics">
      <section className="cargo-metrics-group">
        <h3 className="cargo-metrics-group-title">Partners &amp; team</h3>
        <div className="cargo-stats-grid cargo-stats-grid-wide">
          <CargoMetricCard
            label="Active partners"
            value={formatCount(stats?.companies, loading)}
            unit={companies === 1 ? 'company' : 'companies'}
            hint={partnerHint}
            icon={Building2}
            tone="gold"
            loading={loading}
          />
          <CargoMetricCard
            label="Portal users"
            value={formatCount(stats?.employees, loading)}
            unit="employees"
            hint="Active cargo manager accounts"
            icon={Users}
            tone="blue"
            loading={loading}
          />
        </div>
      </section>

      <section className="cargo-metrics-group">
        <h3 className="cargo-metrics-group-title">Revenue</h3>
        <div className="cargo-stats-grid cargo-stats-grid-wide">
          <CargoMetricCard
            label="Paid volume"
            value={volume.value}
            unit={volume.unit}
            hint="Sum of settled invoice deposits"
            icon={Coins}
            tone="neutral"
            loading={loading}
          />
          <CargoMetricCard
            label="Platform commission"
            value={commission.value}
            unit={commission.unit}
            hint="1.2% fee on paid invoices"
            chip="1.2%"
            icon={Coins}
            tone="green"
            loading={loading}
          />
        </div>
      </section>

      <section className="cargo-metrics-group">
        <h3 className="cargo-metrics-group-title">Invoices</h3>
        <div className="cargo-stats-grid cargo-stats-grid-wide">
          <CargoMetricCard
            label="All invoices"
            value={formatCount(stats?.invoices, loading)}
            unit="total"
            hint={`${pendingInvoices.toLocaleString()} open · ${paidInvoices.toLocaleString()} paid`}
            icon={FileText}
            tone="neutral"
            loading={loading}
          />
          <CargoMetricCard
            label="Paid"
            value={formatCount(stats?.paid, loading)}
            unit={paidInvoices === 1 ? 'invoice' : 'invoices'}
            hint="Payment received · split may still run"
            chip={totalInvoices > 0 ? `${paidRate}% of total` : undefined}
            icon={CheckCircle2}
            tone="green"
            loading={loading}
            progress={paidRate}
          />
          <CargoMetricCard
            label="Pending payment"
            value={formatCount(stats?.pending, loading)}
            unit="awaiting USDT"
            hint="Open links · customer has not paid yet"
            icon={Clock}
            tone="amber"
            loading={loading}
            warn={pendingInvoices > 0}
          />
        </div>
      </section>

      {issueCount > 0 || !loading ? (
        <section className="cargo-metrics-group">
          <h3 className="cargo-metrics-group-title">
            {issueCount > 0 ? 'Needs attention' : 'Health checks'}
          </h3>
          <div className="cargo-stats-grid cargo-stats-grid-wide">
            <CargoMetricCard
              label="Split failed"
              value={formatCount(stats?.split_failed, loading)}
              unit={splitFailed === 1 ? 'invoice' : 'invoices'}
              hint={splitFailed > 0 ? 'Retry split from invoice detail' : 'No failed splits'}
              icon={AlertTriangle}
              tone="red"
              loading={loading}
              warn={splitFailed > 0}
            />
            <CargoMetricCard
              label="Compliance blocked"
              value={formatCount(stats?.aml_failed, loading)}
              unit="aml_failed"
              hint={amlBlocked > 0 ? 'Contact office@onyxpay.biz' : 'No blocked payments'}
              icon={AlertTriangle}
              tone="red"
              loading={loading}
              warn={amlBlocked > 0}
            />
            <CargoMetricCard
              label="Missing payout wallet"
              value={formatCount(stats?.no_wallet, loading)}
              unit={noWallet === 1 ? 'partner' : 'partners'}
              hint={noWallet > 0 ? 'Active partners without Tron wallet' : 'All active partners configured'}
              icon={Wallet}
              tone="amber"
              loading={loading}
              warn={noWallet > 0}
            />
          </div>
        </section>
      ) : null}
    </div>
  );
}
